import type {
    HolisticSummaryCategory,
    SpendAccountCategory,
} from '@fruitful/types';
import * as React from 'react';
import { useRef, useState } from 'react';
import { Keyboard, type TextInput } from 'react-native';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    ChevronDownIcon,
    FootnoteRegular,
    Icon,
    Private,
    Separator,
    StyledBodyMediumEmphasizedInput,
    StyledH1EmphasizedInput,
    useTheme,
    XStack,
    YStack,
} from 'smoothie';

import { isEmpty } from '../../../common/misc';
import type { ContainerComponent } from '../../../common/types/Common';
import {
    centsToFormattedDollars,
    formatCurrencyInputOnChange,
} from '../../../common/utils/bankingUtils';
import type { IManualAccount } from '../../types/ManualAccount';
import CategorySelect from './CategorySelect';

const Amount = ({
    amount,
    onChange,
}: {
    amount: string | undefined;
    onChange: (amountString: string) => void;
}) => {
    const inputRef = useRef<TextInput>(null);
    const amt = amount ?? '';
    return (
        <YStack marginTop={16}>
            <StyledH1EmphasizedInput
                ref={inputRef}
                placeholder="$0.00"
                enterKeyHint="done"
                autoComplete="off"
                autoCorrect={false}
                inputMode="decimal"
                keyboardType="decimal-pad"
                value={amt}
                onChangeText={(text: string) => {
                    onChange(formatCurrencyInputOnChange(amt, text));
                }}
                width="70%"
                textAlign="left"
                paddingLeft={0}
            />
            <FootnoteRegular color="$gray900" marginTop={8}>
                Value
            </FootnoteRegular>
        </YStack>
    );
};

const RowWrapper: ContainerComponent = ({ children }) => (
    <XStack
        width="100%"
        justifyContent="space-between"
        alignItems="center"
        height={50}
        gap={32}
    >
        {children}
    </XStack>
);

const InstitutionName = ({
    name,
    onChange,
}: {
    name: string | undefined;
    onChange: (name: string) => void;
}) => {
    const inputRef = useRef<TextInput>(null);

    return (
        <RowWrapper>
            <BodyMediumRegular>Institution</BodyMediumRegular>
            <StyledBodyMediumEmphasizedInput
                placeholder="Institution name"
                enterKeyHint="done"
                ref={inputRef}
                width={220}
                textAlign="right"
                value={name ?? ''}
                onChangeText={onChange}
            />
        </RowWrapper>
    );
};

const Category = ({
    category,
    onPress,
    spendType,
}: {
    category?: HolisticSummaryCategory;
    spendType?: SpendAccountCategory;
    onPress: () => void;
}) => {
    const theme = useTheme();
    const display =
        category != null && spendType != null
            ? `${category} (${spendType})`
            : (category ?? 'Choose a category');
    return (
        <RowWrapper>
            <BodyMediumRegular>Category</BodyMediumRegular>
            <XStack
                alignItems="center"
                gap={6}
                onPress={onPress}
                cursor="pointer"
                height="100%"
                paddingRight={4} // matches padding of inputs above and below this one
                paddingLeft={16}
                borderRadius={12}
                hoverStyle={{
                    backgroundColor: '$gray100',
                }}
            >
                <BodyMediumEmphasized
                    textAlign="right"
                    color={
                        category != null ? undefined : theme.placeholderColor
                    }
                    textTransform={category != null ? 'capitalize' : 'none'}
                >
                    {display}
                </BodyMediumEmphasized>
                <Icon icon={ChevronDownIcon} size={16} color="$gray1200" />
            </XStack>
        </RowWrapper>
    );
};

const Name = ({
    name,
    onChange,
}: {
    name: string | undefined;
    onChange: (name: string) => void;
}) => {
    const inputRef = useRef<TextInput>(null);

    return (
        <RowWrapper>
            <BodyMediumRegular>Name</BodyMediumRegular>
            <StyledBodyMediumEmphasizedInput
                placeholder="Account name"
                enterKeyHint="done"
                ref={inputRef}
                width={220}
                textAlign="right"
                value={name ?? ''}
                onChangeText={onChange}
            />
        </RowWrapper>
    );
};

interface EditorProps {
    account?: Partial<Omit<IManualAccount, 'id'>>;
    submitAction: (manualAccount: Partial<IManualAccount>) => void;
    submitLabel: string;
    canDelete?: boolean;
    deleteAction?: () => void;
}

const ManualAccountEditor = (props: EditorProps) => {
    const { account, submitAction, submitLabel } = props;

    const canDelete = 'canDelete' in props ? props.canDelete : false;
    const deleteAction =
        'deleteAction' in props ? props.deleteAction : () => {};

    const [isCategorySheetOpen, setIsCategorySheetOpen] =
        useState<boolean>(false);

    const [amount, setAmount] = useState<string | undefined>(
        account?.amount != null
            ? centsToFormattedDollars(account?.amount)
            : undefined,
    );
    const [institutionName, setInstitutionName] = useState<string | undefined>(
        account?.institutionName,
    );

    const [name, setName] = useState<string | undefined>(account?.name);
    const [category, setCategory] = useState<
        HolisticSummaryCategory | undefined
    >(account?.holisticCategory as HolisticSummaryCategory);

    const [spendType, setSpendType] = useState<SpendAccountCategory | null>(
        account?.type as SpendAccountCategory,
    );

    const isValid =
        amount != null && amount !== '' && !isEmpty(name) && !isEmpty(category);

    return (
        <YStack
            width="100%"
            height="100%"
            $platform-web={{
                height: '50vh',
                minHeight: 400,
                maxHeight: 600,
                maxWidth: '100vw',
            }}
            justifyContent="space-between"
        >
            <YStack gap={2}>
                <Private>
                    <Amount
                        amount={amount}
                        onChange={(amountString) => {
                            setAmount(amountString);
                        }}
                    />
                </Private>
                <Separator borderBottomWidth={0} marginVertical={24} />
                <Name
                    name={name}
                    onChange={(name) => {
                        setName(name);
                    }}
                />
                <Separator borderColor="$gray200" />
                <Category
                    onPress={() => {
                        Keyboard.dismiss();
                        setIsCategorySheetOpen(true);
                    }}
                    category={category as HolisticSummaryCategory}
                    spendType={spendType ?? undefined}
                />
                <Separator borderColor="$gray200" />
                <InstitutionName
                    name={institutionName}
                    onChange={(institutionName) => {
                        setInstitutionName(institutionName);
                    }}
                />
            </YStack>
            <YStack gap={8}>
                {canDelete && (
                    <Button
                        variant={ButtonVariant.TERTIARY}
                        tone="danger"
                        size={ButtonSize.LARGE}
                        text="Delete"
                        onPress={deleteAction}
                    />
                )}
                <Button
                    variant={ButtonVariant.PRIMARY}
                    tone={canDelete ? 'neutral' : 'positive'}
                    size={ButtonSize.LARGE}
                    text={submitLabel}
                    disabled={!isValid}
                    onPress={() => {
                        const amtToSubmit =
                            amount != null
                                ? Math.trunc(
                                      parseFloat(
                                          amount.replace(/[^0-9.]/g, ''),
                                      ) * 100,
                                  )
                                : undefined;

                        submitAction({
                            ...account,
                            amount: amtToSubmit,
                            institutionName,
                            name,
                            holisticCategory: category,
                            type: spendType,
                        });
                    }}
                />
            </YStack>
            <CategorySelect
                isOpen={isCategorySheetOpen}
                onClose={() => {
                    setIsCategorySheetOpen(false);
                }}
                onUpdate={(category, updateSpendType) => {
                    setCategory(category as HolisticSummaryCategory);
                    setSpendType(updateSpendType);
                }}
                accountCategory={category}
                accountSpendType={(spendType ?? null) as SpendAccountCategory}
            />
        </YStack>
    );
};

export default ManualAccountEditor;
